'use client';

/**
 * Intent Add Schedule Section
 *
 * @component_role Explorer section type `intent_add_schedule`.
 *
 * @business_rule Capability-gated action. Opens the Add Schedule form
 *   on the canvas as a dedicated tab, mirroring the Add Product flow
 *   in discovery. Gate reads `strategyName('intent', 'ingest_schedule')`
 *   — hidden when the caller can't create schedules.
 *
 * @layer presentation
 * @arch_component_type section
 * @arch_domain discovery (intent)
 */

import { FileUp } from 'lucide-react';
import { useTabStore } from '@/lib/stores/tab-store';
import { cn } from '@/lib/utils/cn';
import type { ExplorerSectionConfig, LabelsDict } from '@/lib/api/types';

// Capability gate deliberately omitted: intent strategies aren't yet
// synced to the `BusinessEntity {domain:'bff_strategies'}` graph nodes
// the capability-store reads, so `strategyName('intent',
// 'ingest_schedule')` returns null and the button would never show.
// Backend scope (data_gateway STRATEGY_SCOPES + JWT) still enforces
// who can actually ingest — the gate is purely UX hiding. Restore it
// once the sync lands.

export function IntentAddScheduleSection({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config,
  labels: L,
}: {
  config: ExplorerSectionConfig;
  labels: LabelsDict;
}) {
  const openTab = useTabStore((s) => s.openTab);
  const label = L.add_schedule_label ?? 'Add Schedule';

  return (
    <div className="px-3 py-2 border-b border-border">
      <button
        type="button"
        onClick={() =>
          openTab({
            id: 'add-schedule-form',
            type: 'add_schedule',
            title: label,
            entityId: 'add-schedule',
          })
        }
        className={cn(
          'w-full py-2 px-3 rounded-md text-sm',
          'flex items-center justify-center gap-2',
          'border-2 border-dashed border-border',
          'text-muted-foreground hover:text-foreground hover:border-primary/50',
          'transition-colors',
        )}
      >
        <FileUp className="w-4 h-4" />
        {label}
      </button>
    </div>
  );
}
